import { callGeminiProxy, cleanGeminiResponse } from './geminiProxy';
import { formatChatStatsForPrompt } from './formatChatStatsForPrompt';
import { formatLeadsSummaryForPrompt } from './formatLeadsSummaryForPrompt';
import type { LeadsAnalysisSummary } from '../../../features/dashboard/lib/buildLeadsSummary';

export interface ChatStats {
    totalConversations: number;
    openConversations: number;
    closedConversations: number;
    unassignedConversations: number;
    conversationsByAgent: { agentName: string; count: number }[];
    /** Origem das conversas (distinto de porCanal das negociações). */
    origemDasConversas: Record<string, number>;
    tempoMedianoPrimeiraRespostaMinutos: number | null;
    pctRespondidasEmAte15Min: number | null;
    conversasSemRespostaDaEquipe: number;
}

export const getSalesAnalysis = async (
    leadsSummary: LeadsAnalysisSummary,
    chatStats?: ChatStats,
    dateRangeText?: string
): Promise<string> => {
    const leadsDataText = formatLeadsSummaryForPrompt(leadsSummary);
    const chatDataText = chatStats
        ? formatChatStatsForPrompt(chatStats)
        : 'Dados de atendimentos não disponíveis para esta análise.';

    const prompt = `
PAPEL: Você é um Consultor Sênior de Estratégia Comercial e Diretor de Vendas.
Sua saída é uma ANÁLISE DECISÓRIA para a diretoria — não um relatório técnico nem um inventário de números.

PERÍODO ANALISADO: ${dateRangeText || 'Período atual'}

DADOS INTERNOS (use para decidir; NÃO copie a estrutura nem os rótulos técnicos abaixo):
--- NEGOCIAÇÕES ---
${leadsDataText}
--- ATENDIMENTOS ---
${chatDataText}
--- FIM DOS DADOS ---

TOM E ESTILO (obrigatório):
1. Escreva como quem orienta a equipe: o que está errado, por quê importa e o que fazer.
2. PROIBIDO soar técnico ou "dashboard": não use frases como "o indicador mostra", "em relação ao total", "percentual de", "métrica", "SLA", "payload".
3. PROIBIDO listar canais só com números empilhados. Integre o dado na frase de decisão.
   Ruim: "{{canal|Meta Ads}}: 20 ganhos, 30 perdidos, R$ 10 mil."
   Bom: "{{canal|Meta Ads}} traz volume, mas perde demais — a equipe precisa qualificar antes de avançar; mesmo assim foi o canal que mais vendeu (cerca de R$ X)."
4. SEJA DECISIVO: "A equipe precisa fazer X." Nunca "pode ser que" / "vale analisar".
5. Português apenas. Nunca inglês ("Leads" → "Clientes Potenciais", "Follow-up" → "Retorno").
6. Sem códigos, IDs ou emojis. Markdown: só ## nos títulos. Listas com "- ". Negrito com **texto**. Parágrafos curtos.
7. BADGES (obrigatórios ao citar): use EXATAMENTE {{perda|Nome}}, {{canal|Nome}}, {{usuario|Nome}} — sem espaços extras dentro. Não invente nomes que não estejam nos dados.
8. Não misture origem de negociação com origem de conversa; deixe claro quando for cada uma.
9. Números (totais, ganhos, perdidos, R$ de vendas, tempo de resposta) entram no texto como apoio à decisão — não como tabela disfarçada.

ESTRUTURA OBRIGATÓRIA (nesta ordem):

## Diagnóstico para a Diretoria (${dateRangeText || ''})
3–5 linhas em prosa: o que aconteceu no período, quem vendeu, onde trava (negociação e atendimento). Use badges {{canal|...}} e {{usuario|...}}. Termine com a prioridade nº 1 da diretoria.

## Gargalos Encontrados
- **Motivos de Perda:** badges {{perda|...}} + o que isso exige mudar no discurso/processo.
- **Origem das Negociações:** com badges {{canal|...}}, diga quem gera volume, quem gera venda (R$), quem gera perda — e a decisão (investir, cortar, corrigir). Inclua ganhos/perdidos/vendas de forma narrativa, não em lista técnica.
- **Usuários:** badges {{usuario|...}} + o que a gestão deve cobrar ou redistribuir.
- **Atendimentos:** total de conversas e origens {{canal|...}} em linguagem de fila/velocidade (demora para iniciar, conversas sem resposta, sem responsável) + a decisão operacional.

## Plano de Ação
- **Ação 1:** [o que fazer]. **Por quê:** [impacto no período].
- **Ação 2:** [o que fazer]. **Por quê:** [impacto no período].
- **Ação 3:** [o que fazer]. **Por quê:** [impacto no período].
    `;

    const rawText = await callGeminiProxy(prompt, { temperature: 0.35 });
    return cleanGeminiResponse(rawText);
};
